import { useState } from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Pagination,
  StyledEngineProvider,
  ThemeProvider,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";

import { formatDate } from "@aclymatepackages/formatters";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { mainTheme } from "@aclymatepackages/themes";
import { DefaultPaper, Select } from "@aclymatepackages/atoms";

import {
  findDefaultDecision,
  findEffectiveCommuteStartDate,
  toStartDateObj
} from "./EmployeeBulkImportPreview.helpers";

const PAGE_SIZE = 25;

const buildDecisionOptions = ({ analysis }) => {
  const baseOptions = [
    { label: "Create new employee", value: "new" },
    { label: "Skip this row", value: "skip" }
  ];

  if (analysis?.identityMatchCandidate) {
    const { name, department } = analysis.identityMatchCandidate;
    return [
      {
        label: `Match: ${name}${department ? ` (${department})` : ""}`,
        value: "match"
      },
      ...baseOptions
    ];
  }

  return baseOptions;
};

const RowStatusChip = ({ analysis }) => {
  const { palette } = useTheme();

  if (!analysis?.success) {
    return (
      <Tooltip title={analysis?.reason || "Row could not be parsed"}>
        <Chip
          icon={<ErrorIcon />}
          size="small"
          label="Invalid"
          sx={{
            backgroundColor: palette.error.light,
            color: palette.error.contrastText
          }}
        />
      </Tooltip>
    );
  }

  if (analysis.officeMatchBlocked) {
    return (
      <Tooltip title="No work-location data in this row — skip or fix CSV">
        <Chip
          icon={<WarningIcon />}
          size="small"
          label="Blocked"
          sx={{
            backgroundColor: palette.error.light,
            color: palette.error.contrastText
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Chip
      icon={<CheckCircleIcon />}
      size="small"
      label="Ready"
      color="success"
      variant="outlined"
    />
  );
};

const OfficeMatchLabel = ({ officeMatch }) => {
  const { palette } = useTheme();

  if (!officeMatch) {
    return (
      <Typography variant="body2" color="error">
        No work location
      </Typography>
    );
  }

  if (officeMatch.source === "matched") {
    return (
      <>
        <Typography variant="body2" color="textPrimary">
          {officeMatch.name || officeMatch.city}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Matched to existing office
        </Typography>
      </>
    );
  }

  return (
    <>
      <Box sx={{ color: palette.warning.dark }}>
        <Typography variant="body2" color="inherit">
          {officeMatch.name}
        </Typography>
      </Box>
      <Typography variant="caption" color="textSecondary">
        From CSV — no office on file
      </Typography>
    </>
  );
};

const DataQualityNotesPopover = ({ notes = [] }) => {
  if (!notes.length) {
    return (
      <Tooltip title="Full survey-quality data — no defaults applied">
        <CheckCircleIcon fontSize="small" color="success" />
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" color="inherit" display="block">
            Defaulted fields:
          </Typography>
          {notes.map((note, idx) => (
            <Typography
              key={`note-${idx}`}
              variant="caption"
              color="inherit"
              display="block"
            >
              • {note}
            </Typography>
          ))}
        </Box>
      }
    >
      <InfoIcon fontSize="small" color="action" />
    </Tooltip>
  );
};

const PreviewSummary = ({ summary }) => {
  const { total = 0, ready = 0, blocked = 0, invalid = 0 } = summary || {};

  const lines = [
    invalid && { severity: "error", text: `${invalid} invalid` },
    blocked && {
      severity: "warning",
      text: `${blocked} blocked (skip or fix)`
    },
    ready && { severity: "success", text: `${ready} ready to import` }
  ].filter(Boolean);

  if (!lines.length) {
    return null;
  }

  return (
    <Box mb={2}>
      <Typography variant="subtitle1" color="textPrimary" gutterBottom>
        Preview of {total} parsed row{total === 1 ? "" : "s"}
      </Typography>
      <Grid container spacing={1}>
        {lines.map(({ severity, text }) => (
          <Grid item key={`summary-${severity}`}>
            <Alert severity={severity} variant="outlined" sx={{ py: 0 }}>
              {text}
            </Alert>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const PreviewRow = ({
  analysis,
  idx,
  decision,
  updateDecision,
  companyStartDate,
  useCompanyStartDateFallback
}) => {
  const { palette } = useTheme();
  const { row = {}, officeMatch, dataQualityNotes = [] } = analysis;
  const { date: effectiveStartDate, source: startDateSource } =
    findEffectiveCommuteStartDate({
      row,
      companyStartDate,
      useCompanyStartDateFallback
    });
  const currentDecision = decision?.action || findDefaultDecision(analysis);
  const decisionOptions = buildDecisionOptions({ analysis });

  const displayName =
    `${row.firstName || ""} ${row.lastName || ""}`.trim() ||
    row.email ||
    "(no name)";

  return (
    <Box
      sx={{
        py: 1.5,
        px: 2,
        backgroundColor:
          idx % 2 === 0
            ? palette.background.paper
            : palette.backgroundGray.light
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <RowStatusChip analysis={analysis} />
            </Grid>
            <Grid item xs>
              <Typography variant="body2" color="textPrimary">
                {displayName}
              </Typography>
              {row.department && (
                <Typography variant="caption" color="textSecondary">
                  {row.department}
                </Typography>
              )}
              <Typography
                variant="caption"
                display="block"
                color={startDateSource === "today" ? "error" : "textSecondary"}
              >
                Commuting counts from {formatDate(effectiveStartDate)}
                {startDateSource === "today" ? " (today)" : ""}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={3}>
          <OfficeMatchLabel officeMatch={officeMatch} />
        </Grid>

        <Grid item xs={10} sm={5}>
          {analysis.success ? (
            <Select
              label="Decision"
              options={decisionOptions}
              value={currentDecision}
              editValue={updateDecision}
              size="small"
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Cannot import
            </Typography>
          )}
        </Grid>

        <Grid item xs={2} sm={1} container justifyContent="flex-end">
          <DataQualityNotesPopover notes={dataQualityNotes} />
        </Grid>
      </Grid>
    </Box>
  );
};

const PreviewRowList = ({
  analyses,
  visibleAnalysisIndices,
  decisions,
  updateDecision,
  currentPage,
  setCurrentPage,
  companyStartDate,
  useCompanyStartDateFallback
}) => {
  const pageCount = Math.max(
    1,
    Math.ceil(visibleAnalysisIndices.length / PAGE_SIZE)
  );
  const safePage = Math.min(currentPage, pageCount);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const visibleSlice = visibleAnalysisIndices.slice(
    pageStart,
    pageStart + PAGE_SIZE
  );

  return (
    <Box>
      <Grid container spacing={2} sx={{ px: 2, py: 1 }} alignItems="center">
        <Grid item xs={12} sm={3}>
          <Typography variant="overline" color="textSecondary">
            Employee
          </Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Typography variant="overline" color="textSecondary">
            Work location
          </Typography>
        </Grid>
        <Grid item xs={12} sm={5}>
          <Typography variant="overline" color="textSecondary">
            Decision
          </Typography>
        </Grid>
        <Grid item xs={12} sm={1} container justifyContent="flex-end">
          <Typography variant="overline" color="textSecondary">
            Notes
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      {visibleSlice.map((absoluteIdx) => (
        <PreviewRow
          key={`preview-row-${absoluteIdx}`}
          analysis={analyses[absoluteIdx]}
          idx={absoluteIdx}
          decision={decisions[absoluteIdx]}
          updateDecision={updateDecision(absoluteIdx)}
          companyStartDate={companyStartDate}
          useCompanyStartDateFallback={useCompanyStartDateFallback}
        />
      ))}
      {pageCount > 1 && (
        <Box mt={2} display="flex" justifyContent="center">
          <Pagination
            count={pageCount}
            page={safePage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

const needsAttention = (analysis) =>
  !analysis.success ||
  analysis.officeMatchBlocked ||
  !!analysis.identityMatchCandidate;

const buildExpandToggleLabel = ({
  rowsExpanded,
  filterToNeedsAttention,
  needsAttentionCount,
  totalCount
}) => {
  if (!rowsExpanded) {
    return `Show all ${totalCount} rows`;
  }
  if (filterToNeedsAttention) {
    return `Showing ${needsAttentionCount} of ${totalCount} — hide rows`;
  }
  return "Hide rows";
};

const countRowsWithCsvStartDate = (analyses = []) =>
  analyses.filter((analysis) => !!toStartDateObj(analysis.row?.startDate))
    .length;

const formatCompanyStartDate = (companyStartDate) => {
  if (!companyStartDate) {
    return null;
  }
  const date = companyStartDate.seconds
    ? new Date(companyStartDate.seconds * 1000)
    : new Date(companyStartDate);
  return formatDate(date);
};

const StartDateFallbackOption = ({
  analyses,
  companyStartDate,
  useCompanyStartDateFallback,
  setUseCompanyStartDateFallback
}) => {
  if (!analyses.length) {
    return null;
  }

  const csvWithDates = countRowsWithCsvStartDate(analyses);
  const csvWithoutDates = analyses.length - csvWithDates;
  const companyStartDisplay = formatCompanyStartDate(companyStartDate);
  const hasCompanyStartDate = !!toStartDateObj(companyStartDate);

  // Rows that will fall all the way through to today, which silently drops
  // their commuting emissions out of every prior reporting year.
  const rowsDefaultingToToday =
    !hasCompanyStartDate || !useCompanyStartDateFallback ? csvWithoutDates : 0;

  return (
    <Box mb={2}>
      {!!csvWithoutDates &&
        hasCompanyStartDate &&
        setUseCompanyStartDateFallback && (
          <FormControlLabel
            control={
              <Checkbox
                checked={useCompanyStartDateFallback}
                onChange={(e) =>
                  setUseCompanyStartDateFallback(e.target.checked)
                }
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="textPrimary">
                For the {csvWithoutDates} row
                {csvWithoutDates === 1 ? "" : "s"} without a start date in the
                CSV, use the company start date ({companyStartDisplay}) so they
                count toward your full reporting period.
              </Typography>
            }
          />
        )}
      {!!csvWithDates && (
        <Typography variant="body2" color="textPrimary">
          {csvWithDates} row{csvWithDates === 1 ? "" : "s"} will use the start
          date from the CSV.
        </Typography>
      )}
      {!!rowsDefaultingToToday && (
        <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
          {rowsDefaultingToToday} row
          {rowsDefaultingToToday === 1 ? "" : "s"} will start counting from
          today, so those employees will produce no commuting emissions for any
          prior reporting year.
          {!hasCompanyStartDate
            ? " This account has no start date set, so there is nothing to fall back to. Add a start date column to the CSV, or set the company start date first."
            : " Add a start date column to the CSV, or check the box above."}
        </Alert>
      )}
    </Box>
  );
};

const EmployeeBulkImportPreview = ({
  previewResult,
  decisions,
  setDecisions,
  companyStartDate,
  useCompanyStartDateFallback,
  setUseCompanyStartDateFallback
}) => {
  const { analyses = [], summary } = previewResult || {};
  const [rowsExpanded, setRowsExpanded] = useState(false);
  const [filterToNeedsAttention, setFilterToNeedsAttention] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const updateDecision = (idx) => (action) => {
    const analysis = analyses[idx];
    const individualId =
      action === "match" ? analysis?.identityMatchCandidate?.id || null : null;
    setDecisions((current) => ({
      ...current,
      [idx]: { action, individualId }
    }));
  };

  const needsAttentionIndices = analyses
    .map((analysis, idx) => (needsAttention(analysis) ? idx : null))
    .filter((idx) => idx !== null);
  const needsAttentionCount = needsAttentionIndices.length;

  const visibleAnalysisIndices =
    rowsExpanded && filterToNeedsAttention
      ? needsAttentionIndices
      : analyses.map((_, idx) => idx);

  const openNeedsAttentionView = () => {
    setRowsExpanded(true);
    setFilterToNeedsAttention(true);
    setCurrentPage(1);
  };

  const clearAttentionFilter = () => {
    setFilterToNeedsAttention(false);
    setCurrentPage(1);
  };

  const expandToggleLabel = buildExpandToggleLabel({
    rowsExpanded,
    filterToNeedsAttention,
    needsAttentionCount,
    totalCount: analyses.length
  });

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={mainTheme}>
        <Box mt={2}>
          <DefaultPaper>
            <PreviewSummary summary={summary} />
            <StartDateFallbackOption
              analyses={analyses}
              companyStartDate={companyStartDate}
              useCompanyStartDateFallback={useCompanyStartDateFallback}
              setUseCompanyStartDateFallback={setUseCompanyStartDateFallback}
            />
            {needsAttentionCount > 0 && (
              <Box mb={2}>
                <Alert
                  severity="info"
                  variant="outlined"
                  action={
                    <Button
                      color="info"
                      size="small"
                      onClick={openNeedsAttentionView}
                    >
                      Review now
                    </Button>
                  }
                >
                  {needsAttentionCount} row
                  {needsAttentionCount === 1 ? "" : "s"} may need your review.
                </Alert>
              </Box>
            )}
            <Box>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item>
                  <Button
                    onClick={() => {
                      setRowsExpanded((current) => !current);
                      clearAttentionFilter();
                    }}
                    startIcon={
                      rowsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                    size="small"
                  >
                    {expandToggleLabel}
                  </Button>
                </Grid>
                {rowsExpanded &&
                  filterToNeedsAttention &&
                  needsAttentionCount < analyses.length && (
                    <Grid item>
                      <Button size="small" onClick={clearAttentionFilter}>
                        Show all {analyses.length} rows
                      </Button>
                    </Grid>
                  )}
                {rowsExpanded &&
                  !filterToNeedsAttention &&
                  analyses.length > PAGE_SIZE && (
                    <Grid item>
                      <Typography variant="caption" color="textSecondary">
                        Showing {Math.min(PAGE_SIZE, analyses.length)} of{" "}
                        {analyses.length} per page
                      </Typography>
                    </Grid>
                  )}
              </Grid>
            </Box>
            {rowsExpanded && (
              <Box mt={1}>
                <PreviewRowList
                  companyStartDate={companyStartDate}
                  useCompanyStartDateFallback={useCompanyStartDateFallback}
                  analyses={analyses}
                  visibleAnalysisIndices={visibleAnalysisIndices}
                  decisions={decisions}
                  updateDecision={updateDecision}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </Box>
            )}
            <Box mt={2}>
              <Typography variant="caption" color="textSecondary">
                Rows you mark &quot;Skip&quot; are ignored on import. Rows tagged &quot;From
                CSV&quot; will be imported using the work city/state in your
                spreadsheet instead of an Aclymate office record.
              </Typography>
            </Box>
          </DefaultPaper>
        </Box>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default EmployeeBulkImportPreview;
